import { 
  getAuth, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  GoogleAuthProvider, 
  signInWithPopup,
  sendPasswordResetEmail,
  updateProfile,
  sendEmailVerification // Import sendEmailVerification
} from 'firebase/auth';

const auth = getAuth();
const googleProvider = new GoogleAuthProvider();

// API base URL for user service endpoints
const API_URL = process.env.REACT_APP_API_URL;

// Debug log for API_URL
console.log('userService: API_URL loaded:', API_URL);
if (!API_URL) {
  console.error('userService: REACT_APP_API_URL environment variable is not set!');
}

/**
 * Call external signup endpoint to create user in backend database
 * @param {Object} userData - User data to send to backend
 * @returns {Promise} - Response from API
 */
const callSignupEndpoint = async (userData) => {
  try {
    console.log('🚀 SIGNUP: Starting callSignupEndpoint to:', `${API_URL}/auth/signup`);
    const response = await fetch(`${API_URL}/auth/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });
    
    console.log('📡 SIGNUP: Response status:', response.status, response.statusText);
    
    if (!response.ok) {
      // Throw error if response is not OK
      const errorBody = await response.text(); // Try to get more info
      console.error(`Backend signup failed: ${response.status} ${response.statusText}`, errorBody);
      throw new Error(`Backend signup failed: ${response.status} ${response.statusText}`);
    }
    
    const responseData = await response.json();
    console.log('✅ SIGNUP: Backend success');
    return responseData;
  } catch (error) {
    console.error('❌ SIGNUP: Error occurred:', error.message);
    // Re-throw the error to be caught by the calling function
    throw error; 
  }
};

/**
 * Call external login endpoint. The endpoint determines if the user exists.
 * Expected backend response structure:
 * Success: { status: "success", statusCode: "login-success", userData: {...} }
 * New User: { status: "fail", statusCode: "user-not-found", message: "User not found." }
 * Other Error: { status: "fail", statusCode: "some-error", message: "Error details." }
 * @param {Object} userData - User data (uid, email, displayName) - Removed photoURL
 * @returns {Promise<object>} - Parsed JSON response from the API
 */
const callLoginEndpoint = async (userData) => {
  let response;
  let responseData = null; // Initialize responseData

  try {
    console.log('Calling login endpoint with data:', userData); 
    response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData), // Pass userData which no longer contains photoURL
    });

    // Try to parse JSON regardless of status code, as backend might send details in body
    try {
      responseData = await response.json();
      console.log('Login endpoint raw response data:', responseData); 
    } catch (jsonError) {
      console.error('Failed to parse login endpoint response JSON:', jsonError);
      // If JSON parsing fails on a non-OK response, throw based on status text
      if (!response.ok) {
        throw new Error(`Backend login failed: ${response.status} ${response.statusText}`);
      }
      // If JSON parsing fails on an OK response, something is wrong with the backend format
      throw new Error('Backend login response format error.');
    }

    // If response is OK, return the data (should have status: "success")
    if (response.ok) {
      return responseData;
    }

    // If response is NOT OK, check if it's a known 'fail' status we handle (like user-not-found)
    if (responseData && responseData.status === 'fail' && responseData.statusCode === 'user-not-found') {
      console.log('Login endpoint indicated user-not-found.');
      return responseData; // Return the specific error object for the caller to handle
    }

    // Otherwise, it's an unhandled backend error
    const errorMessage = responseData?.message || response.statusText;
    console.error(`Backend login failed: ${response.status} ${errorMessage}`, responseData);
    throw new Error(`Backend login failed: ${errorMessage}`);

  } catch (error) {
    // Catch fetch errors or errors thrown above
    console.error('Backend login error:', error);
    // Re-throw the error to be caught by the calling function (signInWithGoogle)
    // Ensure it's an Error object
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error(String(error));
    }
  }
};

/**
 * Sign up a new user with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {string} displayName - User's display name
 * @param {boolean} ageConfirmed - Whether the user confirmed they are 18+
 * @returns {Promise<UserCredential>} - Firebase User credential
 */
export const signUpWithEmail = async (email, password, displayName, ageConfirmed) => { 
  try {
    console.log('🎯 SIGNUP: Starting signUpWithEmail for:', email);
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user; // Get user object
    
    await updateProfile(user, { displayName: displayName }); 
    
    const backendUserData = {
      uid: user.uid,
      email,
      displayName,
      ageConfirmed: ageConfirmed, 
      method: 'email', 
    };

    // Call external signup endpoint
    await callSignupEndpoint(backendUserData);

    // Send verification email
    try {
      await sendEmailVerification(user);
      console.log('signUpWithEmail: Verification email sent.');
    } catch (verificationError) {
      console.error('signUpWithEmail: Failed to send verification email:', verificationError);
      // Don't block signup if email sending fails, can be resent
    }

    console.log('signUpWithEmail: Firebase Auth & Backend Signup SUCCESS');
    return userCredential; 
  } catch (error) {
    console.error("Error during sign up process (userService):", error);
    // Let the UI handler decide what to do, just re-throw
    throw error; 
  }
};

/**
 * Sign in with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<UserCredential>} - Firebase User credential
 */
export const signInWithEmail = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    const backendUserData = {
      uid: userCredential.user.uid,
      email: userCredential.user.email,
      displayName: userCredential.user.displayName,
    };

    // Call external login endpoint (which now handles Firestore update)
    await callLoginEndpoint(backendUserData);
    
    console.log('signInWithEmail: Firebase Auth & Backend Login SUCCESS');
    return userCredential; // Return credential on full success
  } catch (error) {
    console.error("Error during sign in process (userService):", error);
    // Let the UI handler decide what to do, just re-throw
    throw error; 
  }
};

/**
 * Sign in with Google. Relies on callLoginEndpoint to check user existence.
 * @returns {Promise<UserCredential | {needsCompletion: boolean, profile: object}>} 
 */
export const signInWithGoogle = async () => {
  try {
    // 1. Authenticate with Google
    const userCredential = await signInWithPopup(auth, googleProvider);
    const user = userCredential.user;
    
    // Data needed for backend login check and potentially for signup completion
    const profileData = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
    };

    // 2. Call backend login endpoint to check status/log in
    const loginResponse = await callLoginEndpoint({
        uid: profileData.uid,
        email: profileData.email,
        displayName: profileData.displayName,
    });

    // 3. Process backend response
    if (loginResponse.status === 'fail' && loginResponse.statusCode === 'user-not-found') {
      // New user: Return completion needed status with profile data (without photoURL)
      console.log('signInWithGoogle: Backend confirmed user-not-found. Returning needsCompletion.');
      return { 
        needsCompletion: true, 
        profile: profileData // Pass profile data (without photoURL) needed for SignUp page
      };
    } else if (loginResponse.status === 'success') {
      // Existing user: Backend confirmed login success.
      console.log('signInWithGoogle: Backend confirmed successful login.');
      return userCredential; // Return credential for successful login
    } else {
      // Handle other 'fail' statuses or unexpected responses from callLoginEndpoint
      console.error('signInWithGoogle: Unhandled backend login response:', loginResponse);
      throw new Error(loginResponse.message || 'Login failed due to an unknown backend issue.');
    }
    
  } catch (error) {
    console.error("Error during Google sign in process (userService):", error);
    // Re-throw error for UI to handle (could be Firebase popup error or backend error)
    throw error; 
  }
};

/**
 * Completes the signup process for a user who initially signed in via Google.
 * Creates backend record and Firestore document.
 * @param {string} uid 
 * @param {string} email 
 * @param {string} displayName 
 * @param {boolean} ageConfirmed 
 * @returns {Promise<void>}
 */
export const completeGoogleSignup = async (uid, email, displayName, ageConfirmed) => { 
  try {
    console.log('completeGoogleSignup: Completing signup for UID:', uid);
    const backendUserData = {
      uid,
      email,
      displayName,
      ageConfirmed,
      method: 'google', // Change provider to method
    };

    // 1. Call external signup endpoint (which now handles Firestore creation)
    await callSignupEndpoint(backendUserData);

    console.log('completeGoogleSignup: Backend Signup SUCCESS for UID:', uid);
    // No need to return userCredential here, auth state is already set by initial Google sign-in
  } catch (error) {
     console.error("Error during Google signup completion (userService):", error);
     // Re-throw error for UI to handle
     throw error;
  }
};

/**
 * Send password reset email
 * @param {string} email - User email
 * @returns {Promise} - Void
 */
export const resetPassword = async (email) => {
  try {
    return await sendPasswordResetEmail(auth, email);
  } catch (error) {
    console.error("Error resetting password:", error);
    throw error;
  }
};

/**
 * Get the current authenticated user
 * @returns {Object|null} - Current user or null
 */
export const getCurrentUser = () => {
  return auth.currentUser;
};

/**
 * Resend the verification email to the current user.
 * @returns {Promise<void>}
 */
export const resendVerificationEmail = async () => {
  const user = auth.currentUser;
  if (user) {
    try {
      await sendEmailVerification(user);
      console.log('resendVerificationEmail: Verification email sent.');
    } catch (error) {
      console.error('resendVerificationEmail: Error sending verification email:', error);
      throw error; // Re-throw to be caught by the UI
    }
  } else {
    throw new Error("No user is currently signed in.");
  }
};

/**
 * Fetch comprehensive user info from the backend
 * @param {string} token - Firebase ID token
 * @returns {Promise<object>} - User data including subscription info
 */
export const getUserInfo = async (token) => {
  console.log('getUserInfo: Starting request with token length:', token ? token.length : 'no token');
  console.log('getUserInfo: API_URL:', API_URL);
  
  if (!API_URL) {
    throw new Error('API_URL is not configured');
  }
  
  if (!token) {
    throw new Error('No token provided');
  }
  
  try {
    console.log('getUserInfo: Making fetch request to:', `${API_URL}/auth/user-info`);
    
    // Add timeout to fetch
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    const response = await fetch(`${API_URL}/auth/user-info`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
      signal: controller.signal
    });

    clearTimeout(timeoutId);
    
    console.log('getUserInfo: Response received');
    console.log('getUserInfo: Response status:', response.status);
    console.log('getUserInfo: Response ok:', response.ok);
    console.log('getUserInfo: Response headers:', Object.fromEntries(response.headers.entries()));

    let result;
    try {
      result = await response.json();
      console.log('getUserInfo: Raw response data:', result);
    } catch (jsonError) {
      console.error('getUserInfo: Failed to parse JSON response:', jsonError);
      const text = await response.text();
      console.error('getUserInfo: Response text:', text);
      throw new Error('Failed to parse response as JSON');
    }

    if (!response.ok) {
      console.error('getUserInfo API Error:', response.status, result);
      throw new Error(result.message || `Failed to get user info: ${response.statusText}`);
    }

    if (!result.success || !result.data) {
      console.error('Invalid getUserInfo response format:', result);
      throw new Error('Invalid response format from user info endpoint');
    }

    console.log('getUserInfo: Successfully returning user data:', result.data);
    return result.data;
  } catch (error) {
    if (error.name === 'AbortError') {
      console.error('getUserInfo: Request timed out');
      throw new Error('Request timed out - server may be unavailable');
    }
    console.error('getUserInfo error:', error);
    console.error('getUserInfo error stack:', error.stack);
    throw error;
  }
};


// Export auth for useAuthState hook (still useful for initial loading state maybe)
export { auth };
